/**
 * Event bar geometry — the ONE place the monthly/yearly grids describe how event bars stack.
 *
 * Why this file exists: the bar's vertical position (EventBar) and the height of the cell that
 * has to contain it (DayPicker) are the same measurement expressed twice. They were previously
 * hardcoded independently — EventBar stacked bars at `(order - 1) * 22 + 28` while the day cell
 * was pinned at 80px — so the third event on a day rendered at 72-92px and spilled over the week
 * below, obscuring the dates there. Deriving both from these constants makes that class of bug
 * impossible.
 *
 * Note the grid CANNOT simply clip the overflow: multi-day bars deliberately overflow their cell
 * horizontally (`width: calc(spanDays * 100% + ...)`), and CSS cannot clip one axis while leaving
 * the other visible. The row must therefore be tall enough for its deepest stack.
 */

export interface EventBarGeometry {
  /** Height of a single event bar, px. */
  barHeight: number;
  /** Vertical gap between stacked bars, px. */
  barGap: number;
  /** Space reserved above the first bar for the day number, px. */
  stackTopOffset: number;
  /** Breathing room below the last bar, px. */
  bottomPadding: number;
}

/** Main monthly grid (DayPicker, non-compact). */
export const eventBarGeometry: EventBarGeometry = {
  barHeight: 20,
  barGap: 2,
  stackTopOffset: 28,
  bottomPadding: 6,
};

/** Dense grids: the yearly view's month columns and the side mini-months. */
export const eventBarGeometryCompact: EventBarGeometry = {
  barHeight: 14,
  barGap: 2,
  stackTopOffset: 24,
  bottomPadding: 4,
};

/** Cell height floor, so a sparse month keeps the roomy look it has today. */
export const MIN_DAY_CELL_HEIGHT = 80;

export const getGeometry = (compact: boolean): EventBarGeometry =>
  compact ? eventBarGeometryCompact : eventBarGeometry;

/** Distance from the top of the cell to the top of the bar in slot `order` (1-based). */
export const getEventBarTop = (order: number, compact = false): number => {
  const g = getGeometry(compact);
  return (Math.max(order, 1) - 1) * (g.barHeight + g.barGap) + g.stackTopOffset;
};

/**
 * Minimum cell height needed to fully contain `stackDepth` stacked bars.
 * Returns the floor when nothing is stacked, so empty weeks are unaffected.
 */
export const getRequiredCellHeight = (stackDepth: number, compact = false): number => {
  if (!Number.isFinite(stackDepth) || stackDepth <= 0) {
    return compact ? 0 : MIN_DAY_CELL_HEIGHT;
  }
  const g = getGeometry(compact);
  const needed = getEventBarTop(stackDepth, compact) + g.barHeight + g.bottomPadding;
  return compact ? needed : Math.max(MIN_DAY_CELL_HEIGHT, needed);
};

export default eventBarGeometry;
